import { observer } from 'mobx-react';
import { Table, Tag, Tooltip, Typography, Empty, Space, Alert } from 'antd';
import {
  SafetyOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  QuestionCircleOutlined,
  StarFilled,
  ClockCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type {
  FoundryVariantDetail,
  FoundryDriftStatus,
  FoundryRetirementSeverity,
} from '@/api/types';
import { colors, typography } from '@/theme/tokens';

dayjs.extend(relativeTime);

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// FoundryVariantsCard — the local↔Foundry mapping panel for a single agent.
//
// Surfaces three layers of information per variant, in priority order:
//   Layer 1: variant inventory   (model + FoundryAgentId + last sync time)
//   Layer 2: drift indicator     (InSync ✓ / Drift / Unknown)
//   Layer 3: retirement warning  (severity-colored chip when applicable)
//
// All three layers come from a single backend round-trip — no live SDK
// calls. Per the project rule "agents load via managers", the data is
// composed by IAIAgentConfigManager.GetAgentOverview which reads
// ai.AiAgentFoundryVariant + ai.AIModel + the static ModelLifecyclePolicy.
//
// Visual contract:
//   - Primary variant gets a star + "Primary" pill (the one ai.AiAgent.FoundryAgentId
//     legacy column points at — the runtime fallback)
//   - Drift chip is the most consequential signal — colored red/amber when
//     the operator's edits haven't been pushed to Foundry yet
//   - Retirement chip escalates: Informational → Warning → Critical based
//     on days remaining; Critical (≤ 30 days or already past) is red
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  variants: FoundryVariantDetail[] | undefined;
  /** Show a different empty state when the agent itself has never been provisioned (no legacy FoundryAgentId either). */
  legacyFoundryAgentId?: string | null;
}

export const FoundryVariantsCard = observer(function FoundryVariantsCard({
  variants,
  legacyFoundryAgentId,
}: Props) {
  if (!variants || variants.length === 0) {
    // Two distinct empty states:
    //   a) Agent has a legacy FoundryAgentId but no variants → odd state,
    //      probably mid-migration; show a hint.
    //   b) Truly never provisioned → show a friendlier "click Save to provision" message.
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <div style={{ maxWidth: 460, margin: '0 auto' }}>
            {legacyFoundryAgentId ? (
              <>
                <Text strong style={{ display: 'block', marginBottom: 6 }}>
                  Legacy single-pointer mapping
                </Text>
                <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.55 }}>
                  This agent has the legacy single Foundry id <Text code>{legacyFoundryAgentId}</Text> but
                  no per-model variant rows. Save the agent again to populate the multi-variant
                  table.
                </Text>
              </>
            ) : (
              <>
                <Text strong style={{ display: 'block', marginBottom: 6 }}>
                  Not provisioned on Azure AI Foundry yet
                </Text>
                <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.55 }}>
                  Saving this agent provisions one Foundry agent version per Foundry-eligible
                  model in your merchant. Each one will appear here with its own Foundry agent id
                  and sync status.
                </Text>
              </>
            )}
          </div>
        }
      />
    );
  }

  // Sort: primary first, then by model name. Keeps the most relevant
  // variant (the one the legacy pointer + most callers use) at the top.
  const rows = [...variants].sort((a, b) => {
    if (a.isPrimary && !b.isPrimary) return -1;
    if (b.isPrimary && !a.isPrimary) return 1;
    return (a.modelDisplayName ?? a.modelName ?? '').localeCompare(
      b.modelDisplayName ?? b.modelName ?? '',
    );
  });

  const columns: ColumnsType<FoundryVariantDetail> = [
    {
      title: 'Model',
      key: 'model',
      render: (_text, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          {row.isPrimary && (
            <Tooltip title="Primary variant — the legacy ai.AiAgent.FoundryAgentId points here. Used as runtime fallback when a request doesn't pin a specific AIModelId.">
              <StarFilled style={{ color: colors.amber[600], fontSize: 12, flexShrink: 0 }} />
            </Tooltip>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{row.modelDisplayName || row.modelName}</div>
            {row.modelDisplayName && row.modelName !== row.modelDisplayName && (
              <Text
                type="secondary"
                style={{ fontSize: 11.5, fontFamily: typography.fontMono }}
              >
                {row.modelName}
              </Text>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Foundry agent id',
      key: 'foundryAgentId',
      render: (_text, row) => (
        <div>
          <Text style={{ fontFamily: typography.fontMono, fontSize: 12.5 }}>
            {row.foundryAgentName}
          </Text>
          {row.foundryAgentVersion && (
            <Tag color="blue" style={{ marginLeft: 6, fontSize: 11, borderRadius: 6 }}>
              v{row.foundryAgentVersion}
            </Tag>
          )}
          {row.deploymentName && (
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                deployment <Text code style={{ fontSize: 11 }}>{row.deploymentName}</Text>
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Sync status',
      key: 'drift',
      width: 180,
      render: (_text, row) => <DriftChip status={row.driftStatus} />,
    },
    {
      title: 'Retirement',
      key: 'retirement',
      width: 200,
      render: (_text, row) => <RetirementChip
        severity={row.retirementSeverity}
        retirementDate={row.retirementDate}
        daysUntil={row.daysUntilRetirement}
      />,
    },
    {
      title: 'Last provisioned',
      key: 'lastProvisioned',
      width: 160,
      render: (_text, row) => (
        <Tooltip title={dayjs(row.lastProvisionedDate).format('YYYY-MM-DD HH:mm:ss [UTC]')}>
          <Text type="secondary" style={{ fontSize: 12.5 }}>
            <ClockCircleOutlined style={{ marginRight: 4 }} />
            {dayjs(row.lastProvisionedDate).fromNow()}
          </Text>
        </Tooltip>
      ),
    },
  ];

  // Tally drift + retirement counts so the header can summarize at-a-glance.
  const driftCount = rows.filter((r) => r.driftStatus === 'Drift').length;
  const unknownCount = rows.filter((r) => r.driftStatus === 'Unknown').length;
  const retirementSoon = rows.filter(
    (r) =>
      r.retirementSeverity === 'Warning' ||
      r.retirementSeverity === 'Critical',
  ).length;

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      {(driftCount > 0 || retirementSoon > 0) && (
        <Alert
          type={retirementSoon > 0 ? 'error' : 'warning'}
          showIcon
          icon={retirementSoon > 0 ? <WarningOutlined /> : <SafetyOutlined />}
          message={
            <Space size={12}>
              {driftCount > 0 && (
                <span>
                  <Text strong>{driftCount}</Text>{' '}
                  {driftCount === 1 ? 'variant' : 'variants'} out of sync — reprovision to apply
                  recent edits.
                </span>
              )}
              {retirementSoon > 0 && (
                <span>
                  <Text strong>{retirementSoon}</Text>{' '}
                  {retirementSoon === 1 ? 'variant' : 'variants'} on a retiring model — plan
                  migration.
                </span>
              )}
            </Space>
          }
          style={{ borderRadius: 10 }}
        />
      )}

      {unknownCount > 0 && driftCount === 0 && retirementSoon === 0 && (
        <Alert
          type="info"
          showIcon
          message={
            <span>
              <Text strong>{unknownCount}</Text>{' '}
              {unknownCount === 1 ? 'variant' : 'variants'} provisioned before drift tracking
              existed. Save the agent once to bootstrap the indicator.
            </span>
          }
          style={{ borderRadius: 10 }}
        />
      )}

      <Table<FoundryVariantDetail>
        size="small"
        rowKey="variantId"
        columns={columns}
        dataSource={rows}
        pagination={false}
      />
    </Space>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// Drift chip — semantic colors per status
// ─────────────────────────────────────────────────────────────────────────────

function DriftChip({ status }: { status: FoundryDriftStatus }) {
  if (status === 'InSync') {
    return (
      <Tooltip title="Local Instructions body matches the hash of what was last sent to Foundry. The runtime is serving exactly what you see in Agent Studio.">
        <Tag
          color="success"
          icon={<CheckCircleOutlined />}
          style={{ borderRadius: 999, fontSize: 11.5 }}
        >
          In sync
        </Tag>
      </Tooltip>
    );
  }
  if (status === 'Drift') {
    return (
      <Tooltip title="Local Instructions have changed since the last successful Foundry sync. The runtime is still serving the older version. Save the agent again to push.">
        <Tag
          color="warning"
          icon={<WarningOutlined />}
          style={{ borderRadius: 999, fontSize: 11.5 }}
        >
          Drift detected
        </Tag>
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Variant was provisioned before drift tracking existed (no recorded hash to compare against). Save the agent once to bootstrap the indicator.">
      <Tag
        color="default"
        icon={<QuestionCircleOutlined />}
        style={{ borderRadius: 999, fontSize: 11.5 }}
      >
        Unknown
      </Tag>
    </Tooltip>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Retirement chip — color escalates with severity
// ─────────────────────────────────────────────────────────────────────────────

function RetirementChip({
  severity,
  retirementDate,
  daysUntil,
}: {
  severity: FoundryRetirementSeverity;
  retirementDate?: string | null;
  daysUntil?: number | null;
}) {
  if (severity === 'None' || retirementDate == null) {
    return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
  }

  const date = dayjs(retirementDate).format('MMM D, YYYY');
  // Past retirement is the loudest case.
  if (daysUntil != null && daysUntil < 0) {
    return (
      <Tooltip title={`Retired on ${date}. Runtime calls to this deployment will fail. Migrate this variant to a current model.`}>
        <Tag color="red" icon={<WarningOutlined />} style={{ borderRadius: 999, fontSize: 11.5 }}>
          Retired {Math.abs(daysUntil)} days ago
        </Tag>
      </Tooltip>
    );
  }

  // Color + label by severity tier.
  const colorByTier: Record<FoundryRetirementSeverity, string> = {
    None: 'default',
    Informational: 'gold',
    Warning: 'orange',
    Critical: 'red',
  };
  const labelPrefix =
    severity === 'Critical' ? 'Retiring soon' : severity === 'Warning' ? 'Retiring' : 'Heads up';

  return (
    <Tooltip
      title={`${labelPrefix} on ${date} (${daysUntil ?? '?'} days). Plan migration to a current model before the runtime starts returning errors.`}
    >
      <Tag
        color={colorByTier[severity]}
        icon={severity === 'Critical' ? <WarningOutlined /> : <SafetyOutlined />}
        style={{ borderRadius: 999, fontSize: 11.5 }}
      >
        {labelPrefix} · {daysUntil}d
      </Tag>
    </Tooltip>
  );
}
